import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  BarChart3, Users, Home, Dog, Heart, Eye, TrendingUp,
  ArrowLeft, RefreshCw, Loader2, ExternalLink, MessageSquare,
  Phone, QrCode, Rocket, Calendar
} from 'lucide-react';
import PanelLayout from '@/components/PanelLayout';

import { API } from '@/utils/api';
import { useTranslation } from 'react-i18next';

export default function AdminStats() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  const [overview, setOverview] = useState(null);
  const [registrationsChart, setRegistrationsChart] = useState([]);
  const [viewsChart, setViewsChart] = useState([]);
  const [topPages, setTopPages] = useState([]);
  const [conversions, setConversions] = useState(null);
  const [utmStats, setUtmStats] = useState(null);
  
  useEffect(() => {
    fetchAllStats();
  }, []);
  
  const fetchAllStats = async () => {
    setRefreshing(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [overviewRes, regChartRes, viewsChartRes, topPagesRes, conversionsRes, utmRes] = await Promise.all([
        axios.get(`${API}/admin/stats/overview`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/stats/registrations-chart?days=7`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/stats/views-chart?days=7`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/stats/top-pages?limit=10`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/stats/conversions`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/stats/utm`, { headers }).catch(() => ({ data: null }))
      ]);
      
      setOverview(overviewRes.data);
      setRegistrationsChart(regChartRes.data || []);
      setViewsChart(viewsChartRes.data || []);
      setTopPages(topPagesRes.data || []);
      setConversions(conversionsRes.data);
      setUtmStats(utmRes.data);
    } catch (e) {
      console.error('Stats fetch error:', e);
      toast.error('Błąd ładowania statystyk');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  // Simple bar chart component
  const SimpleBarChart = ({ data, dataKey, color = "amber" }) => {
    const maxValue = Math.max(...data.map(d => d[dataKey]), 1);
    
    return (
      <div className="flex items-end gap-2 h-32">
        {data.map((d, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <div 
              className={`w-full bg-${color}-500 rounded-t transition-all`}
              style={{ height: `${(d[dataKey] / maxValue) * 100}%`, minHeight: d[dataKey] > 0 ? '4px' : '0' }}
            />
            <span className="text-xs text-zinc-500">{d.date}</span>
          </div>
        ))}
      </div>
    );
  };
  
  if (loading) {
    return (
      <PanelLayout>
        <div className="min-h-screen bg-[#FDF8F3] flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
        </div>
      </PanelLayout>
    );
  }
  
  return (
    <PanelLayout>
      <div className="min-h-screen bg-[#FDF8F3] text-zinc-900">
        {/* Header */}
        <div className="border-b border-zinc-200 bg-white">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Link to="/panel" className="text-zinc-500 hover:text-zinc-900">
                  <ArrowLeft className="w-5 h-5" />
                </Link>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <BarChart3 className="w-6 h-6 text-orange-500" />
                  Admin Stats
                </h1>
                <p className="text-zinc-500 text-sm">Statystyki platformy</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={fetchAllStats}
                disabled={refreshing}
                className="border-zinc-200"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Odśwież
              </Button>
              <Link to="/admin/booster">
                <Button className="bg-orange-500 hover:bg-orange-600 text-black">
                  <Rocket className="w-4 h-4 mr-2" />
                  Booster
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Overview Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {/* Users */}
          <div className="bg-[#FDF8F3] rounded-2xl border border-zinc-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-400" />
              </div>
              <span className="text-zinc-500">Użytkownicy</span>
            </div>
            <p className="text-3xl font-bold">{overview?.users?.total || 0}</p>
            <div className="flex items-center gap-2 mt-2">
              <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">
                +{overview?.users?.today || 0} dziś
              </Badge>
              <span className="text-zinc-500 text-xs">
                +{overview?.users?.this_week || 0} / 7 dni
              </span>
            </div>
          </div>
          
          {/* Kennels */}
          <div className="bg-[#FDF8F3] rounded-2xl border border-zinc-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
                <Home className="w-5 h-5 text-orange-500" />
              </div>
              <span className="text-zinc-500">Hodowle</span>
            </div>
            <p className="text-3xl font-bold">{overview?.content?.kennels || 0}</p>
            <div className="flex items-center gap-4 mt-2 text-zinc-500 text-xs">
              <span>🐕 {overview?.content?.dogs || 0} psów</span>
            </div>
          </div>
          
          {/* Litters & Studs */}
          <div className="bg-[#FDF8F3] rounded-2xl border border-zinc-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-pink-500/20 flex items-center justify-center">
                <Heart className="w-5 h-5 text-pink-400" />
              </div>
              <span className="text-zinc-500">Mioty & Study</span>
            </div>
            <p className="text-3xl font-bold">{(overview?.content?.litters || 0) + (overview?.content?.studs || 0)}</p>
            <div className="flex items-center gap-4 mt-2 text-zinc-500 text-xs">
              <span>🍼 {overview?.content?.litters || 0} miotów</span>
              <span>♂ {overview?.content?.studs || 0} studów</span>
            </div>
          </div>
          
          {/* Views */}
          <div className="bg-[#FDF8F3] rounded-2xl border border-zinc-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                <Eye className="w-5 h-5 text-purple-400" />
              </div>
              <span className="text-zinc-500">Wyświetlenia</span>
            </div>
            <p className="text-3xl font-bold">{overview?.views?.total || 0}</p>
            <Badge className="bg-emerald-500/20 text-emerald-400 text-xs mt-2">
              +{overview?.views?.today || 0} dziś
            </Badge>
          </div>
        </div>
        
        {/* Charts Row */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Registrations Chart */}
          <div className="bg-[#FDF8F3] rounded-2xl border border-zinc-200 p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-orange-500" />
              Rejestracje (7 dni)
            </h2>
            {registrationsChart.length > 0 ? (
              <div className="space-y-4">
                <div className="flex items-end gap-2 h-32">
                  {registrationsChart.map((d, i) => {
                    const maxVal = Math.max(...registrationsChart.map(x => x.registrations), 1);
                    const height = (d.registrations / maxVal) * 100;
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1">
                        <span className="text-xs text-orange-500">{d.registrations}</span>
                        <div 
                          className="w-full bg-orange-500 rounded-t transition-all"
                          style={{ height: `${height}%`, minHeight: d.registrations > 0 ? '8px' : '2px' }}
                        />
                        <span className="text-xs text-zinc-500">{d.date}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="text-center text-zinc-500 text-sm">
                  Łącznie: {registrationsChart.reduce((sum, d) => sum + d.registrations, 0)} nowych użytkowników
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-zinc-500">Brak danych</div>
            )}
          </div>
          
          {/* Views Chart */}
          <div className="bg-[#FDF8F3] rounded-2xl border border-zinc-200 p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Eye className="w-5 h-5 text-purple-400" />
              Wyświetlenia (7 dni)
            </h2>
            {viewsChart.length > 0 ? (
              <div className="space-y-4">
                <div className="flex items-end gap-2 h-32">
                  {viewsChart.map((d, i) => {
                    const maxVal = Math.max(...viewsChart.map(x => x.views), 1);
                    const height = (d.views / maxVal) * 100;
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1">
                        <span className="text-xs text-purple-400">{d.views}</span>
                        <div 
                          className="w-full bg-purple-500 rounded-t transition-all"
                          style={{ height: `${height}%`, minHeight: d.views > 0 ? '8px' : '2px' }}
                        />
                        <span className="text-xs text-zinc-500">{d.date}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="text-center text-zinc-500 text-sm">
                  Łącznie: {viewsChart.reduce((sum, d) => sum + d.views, 0)} wyświetleń
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-zinc-500">Brak danych</div>
            )}
          </div>
        </div>
        
        {/* Bottom Row */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Top 10 Pages */}
          <div className="bg-[#FDF8F3] rounded-2xl border border-zinc-200 p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-orange-500" />
              Top 10 stron
            </h2>
            {topPages.length > 0 ? (
              <div className="space-y-3">
                {topPages.map((page, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                    <div className="flex items-center gap-3">
                      <span className="text-zinc-500 text-sm w-6">{i + 1}.</span>
                      <div>
                        <p className="text-zinc-900 font-medium">{page.name}</p>
                        <p className="text-zinc-500 text-xs">{page.type}</p>
                      </div>
                    </div>
                    <Badge className="bg-purple-500/20 text-purple-400">
                      {page.views} views
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-zinc-500">Brak danych</div>
            )}
          </div>
          
          {/* Conversions */}
          <div className="bg-[#FDF8F3] rounded-2xl border border-zinc-200 p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-emerald-400" />
              Konwersje (kliknięcia CTA)
            </h2>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-[#FDF8F3] rounded-xl">
                <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-2">
                  <MessageSquare className="w-6 h-6 text-green-400" />
                </div>
                <p className="text-2xl font-bold text-green-400">{conversions?.whatsapp || 0}</p>
                <p className="text-xs text-zinc-500">WhatsApp</p>
              </div>
              <div className="text-center p-4 bg-[#FDF8F3] rounded-xl">
                <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-2">
                  <Phone className="w-6 h-6 text-blue-400" />
                </div>
                <p className="text-2xl font-bold text-blue-400">{conversions?.phone || 0}</p>
                <p className="text-xs text-zinc-500">Telefon</p>
              </div>
              <div className="text-center p-4 bg-[#FDF8F3] rounded-xl">
                <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center mx-auto mb-2">
                  <QrCode className="w-6 h-6 text-orange-500" />
                </div>
                <p className="text-2xl font-bold text-orange-500">{conversions?.qr || 0}</p>
                <p className="text-xs text-zinc-500">QR Code</p>
              </div>
            </div>
            
            <div className="mt-6 p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/20">
              <div className="flex items-center justify-between">
                <span className="text-zinc-600">Łącznie konwersji</span>
                <span className="text-2xl font-bold text-emerald-400">{conversions?.total || 0}</span>
              </div>
            </div>
            
            {/* Booster stats */}
            <div className="mt-4 p-4 bg-orange-500/10 rounded-xl border border-amber-500/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Rocket className="w-5 h-5 text-orange-500" />
                  <span className="text-zinc-600">Booster posty</span>
                </div>
                <div className="text-right">
                  <p className="text-xl font-bold text-orange-500">{overview?.booster?.total_posts || 0}</p>
                  <p className="text-xs text-zinc-500">+{overview?.booster?.today_posts || 0} dziś</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* UTM Tracking - Booster Traffic */}
        <div className="mt-8 bg-[#FDF8F3] rounded-2xl border border-zinc-200 p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Rocket className="w-5 h-5 text-orange-500" />
            Ruch z Boostera (UTM Tracking)
          </h2>
          
          <div className="mb-4 p-4 bg-orange-500/10 rounded-xl border border-amber-500/20">
            <div className="flex items-center justify-between">
              <span className="text-zinc-600">Łącznie wejść z Boostera</span>
              <span className="text-2xl font-bold text-orange-500">{utmStats?.total_from_booster || 0}</span>
            </div>
          </div>
          
          {utmStats?.campaigns?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FDF8F3]">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm text-zinc-500">Kampania</th>
                    <th className="text-left px-4 py-3 text-sm text-zinc-500">Medium</th>
                    <th className="text-right px-4 py-3 text-sm text-zinc-500">Wyświetlenia</th>
                    <th className="text-right px-4 py-3 text-sm text-zinc-500">Strony</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {utmStats.campaigns.map((c, i) => (
                    <tr key={i} className="hover:bg-[#FDF8F3]">
                      <td className="px-4 py-3 font-medium text-zinc-900">{c.campaign}</td>
                      <td className="px-4 py-3 text-zinc-500">{c.medium}</td>
                      <td className="px-4 py-3 text-right">
                        <Badge className="bg-orange-500/20 text-orange-500">{c.views}</Badge>
                      </td>
                      <td className="px-4 py-3 text-right text-zinc-500">{c.unique_pages}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-zinc-500">
              <p>Brak danych UTM</p>
              <p className="text-xs mt-2">Linki z Boostera automatycznie dodają parametry UTM do śledzenia ruchu</p>
            </div>
          )}
        </div>
      </div>
    </div>
    </PanelLayout>
  );
}
